import { createAction } from 'redux-actions'
import { identity, get } from 'lodash/fp'
import { Action, IHash, ThunkAction } from '../types/common'
import {
  ILocation,
  ILocationCreateAttributes,
  ILocationUpdateAttributes,
  IAssignableUser,
  IUserOption,
} from '../types/location'
import { query } from '../common/api'
import extractErrors from '../common/extractErrors'
import {
  LOCATIONS_QUERY,
  LOCATION_QUERY,
  LOCATION_WITH_ITEMS_QUERY,
  BRANCH_INVENTORY_PRODUCTS_SEARCH_QUERY,
  INTERNAL_BRANCH_INVENTORY_PRODUCTS_SEARCH_QUERY,
  CREATE_LOCATION_MUTATION,
  UPDATE_LOCATION_MUTATION,
  DELETE_LOCATION_MUTATION,
  MANAGER_LOCATION_ASSIGNABLE_USERS_QUERY,
  INTERNAL_LOCATION_ASSIGNABLE_USERS_QUERY,
  CREATE_LOCATION_ITEM_MUTATION,
  UPDATE_LOCATION_ITEM_MUTATION,
  DELETE_LOCATION_ITEM_MUTATION,
  UPLOAD_LOCATIONS_QUERY,
  EXPORT_LOCATIONS_QUERY,
} from './locationsQueries'
import { currentUserSelector } from '../auth/authSelectors'
import { ILocationItem } from '../types/location'
import { IProduct } from '../types/products'

export const SET_LOCATIONS = 'LOCATIONS/SET_LOCATIONS'
export type SET_LOCATIONS = Action<ILocation[]>
export const setLocations = createAction<ILocation[], ILocation[]>(
  SET_LOCATIONS,
  identity,
)

export const SET_CURRENT_LOCATION = 'LOCATIONS/SET_CURRENT_LOCATION'
export type SET_CURRENT_LOCATION = Action<ILocation>
export const setCurrentLocation = createAction<ILocation, ILocation>(
  SET_CURRENT_LOCATION,
  identity,
)

export const UPDATE_LOCATION = 'LOCATIONS/UPDATE_LOCATION'
export type UPDATE_LOCATION = Action<ILocation>
export const updateLocationInStore = createAction<ILocation, ILocation>(
  UPDATE_LOCATION,
  identity,
)

export const REMOVE_LOCATION = 'LOCATIONS/REMOVE_LOCATION'
export type REMOVE_LOCATION = Action<number>
export const removeLocationFromStore = createAction<number, number>(
  REMOVE_LOCATION,
  identity,
)

// tslint:disable-next-line:max-line-length
export const fetchLocations = (
  accountId: number,
  branchId?: number,
): ThunkAction<Promise<ILocation[]>> => {
  return async (dispatch) => {
    try {
      const response = await query(LOCATIONS_QUERY, { accountId, branchId })
      const locations = response.locations || []
      dispatch(setLocations(locations))
      return locations
    } catch (e) {
      console.error('Error fetching locations:', e)
      throw new Error(e.message)
    }
  }
}

export const fetchLocation = (id: number): ThunkAction<Promise<ILocation>> => {
  return async (dispatch) => {
    try {
      const response = await query(LOCATION_QUERY, { id })
      const location = response.location
      if (location) {
        dispatch(setCurrentLocation(location))
      }
      return location
    } catch (e) {
      console.error('Error fetching location:', e)
      throw new Error(e.message)
    }
  }
}

export const fetchLocationWithItems = (
  id: number,
  branchId?: number,
): ThunkAction<
  Promise<{
    id: number
    code: string
    locationType?: string
    locationItems: any[]
    branches?: Array<{ id: number; name: string }>
    totalValue?: number
    hasCycleCounts?: boolean
  }>
> => {
  return async () => {
    try {
      const response = await query(LOCATION_WITH_ITEMS_QUERY, { id, branchId })
      return response.location
    } catch (e) {
      console.error('Error fetching location with items:', e)
      throw new Error(e.message)
    }
  }
}

export const searchInventoryProducts = (
  branchId: number,
  term: string,
  accountId?: number,
): ThunkAction<Promise<IProduct[]>> => {
  return async (dispatch, getState) => {
    const user = currentUserSelector(getState())
    const isInternal = user?.role === 'internal'
    const queryString = isInternal
      ? INTERNAL_BRANCH_INVENTORY_PRODUCTS_SEARCH_QUERY
      : BRANCH_INVENTORY_PRODUCTS_SEARCH_QUERY

    const response = await query(queryString, {
      branchId,
      accountId,
      term,
    })

    const products = isInternal
      ? get(
          'internal.accounts.nodes[0].currentBranch[0].inventoryProducts',
          response,
        )
      : get('manager.currentBranch[0].inventoryProducts', response)

    // New API nests product identity under `product`; flatten so components stay flat (IProduct).
    return ((products || []) as any[]).map((ip) =>
      ip && ip.product ? { ...ip, ...ip.product } : ip,
    ) as IProduct[]
  }
}

export const createLocation = (
  attributes: ILocationCreateAttributes,
): ThunkAction<Promise<IHash<string> | null>> => {
  return async (dispatch) => {
    try {
      const { result, errors } = get(
        'locationCreate',
        await query(CREATE_LOCATION_MUTATION, { attributes }),
      )

      if (errors) {
        return errors
      }

      if (result) {
        dispatch(updateLocationInStore(result))
      }

      return null
    } catch (e) {
      console.error('Error creating location:', e)
      throw new Error(e.message)
    }
  }
}

export const updateLocation = (
  id: number,
  attributes: ILocationUpdateAttributes,
): ThunkAction<Promise<IHash<string> | null>> => {
  return async (dispatch) => {
    try {
      const { result, errors } = get(
        'locationUpdate',
        await query(UPDATE_LOCATION_MUTATION, { id, attributes }),
      )

      if (errors) {
        return errors
      }

      if (result) {
        dispatch(updateLocationInStore(result))
      }

      return null
    } catch (e) {
      console.error('Error updating location:', e)
      throw new Error(e.message)
    }
  }
}

export const deleteLocation = (
  id: number,
): ThunkAction<Promise<IHash<string> | null>> => {
  return async (dispatch) => {
    try {
      const { result, errors } = get(
        'locationDelete',
        await query(DELETE_LOCATION_MUTATION, { id }),
      )

      if (errors) {
        return errors
      }

      if (result) {
        dispatch(removeLocationFromStore(id))
      }

      return null
    } catch (e) {
      console.error('Error deleting location:', e)
      throw new Error(e.message)
    }
  }
}

export const createLocationItem = (attributes: {
  locationId: number
  productId: number
  inventory: number
  price: number
  unit: string
}): ThunkAction<Promise<{ result?: ILocationItem; errors?: string }>> => {
  return async () => {
    try {
      const { result, errors } = get(
        'locationItemCreate',
        await query(CREATE_LOCATION_ITEM_MUTATION, {
          attributes: {
            locationId: attributes.locationId,
            productId: attributes.productId,
            inventory: attributes.inventory,
            price: attributes.price,
            unit: attributes.unit,
          },
        }),
      )

      if (errors) {
        return { errors: extractErrors(errors) }
      }

      return { result }
    } catch (e) {
      console.error('Error creating location item:', e)
      return { errors: e.message }
    }
  }
}

export const deleteLocationItem = (
  id: number,
): ThunkAction<Promise<{ success: boolean; errors?: string }>> => {
  return async () => {
    try {
      const { result, errors } = get(
        'locationItemDelete',
        await query(DELETE_LOCATION_ITEM_MUTATION, { id }),
      )

      if (errors) {
        return { success: false, errors: extractErrors(errors) }
      }

      return { success: !!result }
    } catch (e) {
      console.error('Error deleting location item:', e)
      return { success: false, errors: e.message }
    }
  }
}

export const updateLocationItem = (
  id: number,
  attributes: { threshold?: number | null },
): ThunkAction<Promise<{ result?: ILocationItem; errors?: string }>> => {
  return async () => {
    try {
      const { result, errors } = get(
        'locationItemUpdate',
        await query(UPDATE_LOCATION_ITEM_MUTATION, {
          id,
          attributes,
        }),
      )

      if (errors) {
        return { errors: extractErrors(errors) }
      }

      return { result }
    } catch (e) {
      console.error('Error updating location item:', e)
      return { errors: e.message }
    }
  }
}

export const fetchLocationAssignableUsers = (
  branchId: number,
  accountId?: number,
): ThunkAction<Promise<IUserOption[]>> => {
  return async (dispatch, getState) => {
    try {
      const currentUser = currentUserSelector(getState())
      const userRole = currentUser?.role

      let users: IAssignableUser[] = []

      if (userRole === 'manager') {
        const response = await query(MANAGER_LOCATION_ASSIGNABLE_USERS_QUERY, {
          branchId,
        })
        users =
          get('manager.currentBranch[0].locationAssignableUsers', response) ||
          []
      } else {
        const response = await query(INTERNAL_LOCATION_ASSIGNABLE_USERS_QUERY, {
          branchId,
          accountId,
        })
        const branches =
          get('internal.accounts.nodes[0].branches', response) || []
        users = get('[0].locationAssignableUsers', branches) || []
      }

      return users.map((user: IAssignableUser) => ({
        value: user.id,
        label: user.name,
      }))
    } catch (e) {
      console.error('Error fetching users:', e)
      return []
    }
  }
}

export const uploadLocations = (
  file: File,
  accountId: number,
  branchId: number,
): ThunkAction<Promise<number | { [rowNum: string]: string[] }>> => {
  return async (dispatch) => {
    try {
      if (!accountId || !branchId) {
        return { base: ['Account or branch not selected'] }
      }

      const response = await query(
        UPLOAD_LOCATIONS_QUERY,
        { accountId: accountId.toString(), branchId: branchId.toString() },
        { file },
      )

      const { locations, errors } = get('loadLocationsCsv', response) || {}

      if (errors) return errors
      if (!locations) return { base: ['No data returned from server'] }

      await dispatch(fetchLocations(accountId, branchId))

      return locations.length
    } catch (e) {
      console.error('Error uploading locations:', e)
      return { base: ['Upload failed'] }
    }
  }
}

export const exportLocations = (
  accountId: number,
  branchId: number,
  branchName?: string,
): ThunkAction<Promise<void>> => {
  return async () => {
    try {
      const response = await query(EXPORT_LOCATIONS_QUERY, {
        accountId,
        branchId,
      })
      const csv = get('locationsCsv', response)

      if (!csv) {
        throw new Error('No data returned')
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      link.setAttribute('href', url)
      const safeName = branchName
        ? branchName.replace(/[^a-z0-9]/gi, '_')
        : branchId
      link.setAttribute('download', `Locations_${safeName}.csv`)
      link.style.visibility = 'hidden'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (e) {
      console.error('Error exporting locations:', e)
      throw new Error('Export failed')
    }
  }
}
